import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { LuChevronUp, LuChevronDown, LuMapPin, LuTriangleAlert, LuNavigation, LuLocateFixed } from 'react-icons/lu';
import MapaLeaflet, { linkRuta, linkWaze } from '../mapa/MapaLeaflet';
import api from '../../services/api';
import ModalShell from '../ui/ModalShell';
import { getTodayISO } from '../../utils/dateUtils';

// Ordenar lo que falta del día (28-oct-2026, backend: ordenes/rehacer_ruta.py). Con parte del día hecho, la app
// propone el mejor orden para lo que queda desde donde estás (GPS del celu, o la última visita que cerraste),
// respetando la franja de cada cliente. Se puede ajustar a mano con ▲▼ (recalcula las horas) y se guarda con
// el motivo, que queda en el historial de cada visita y le llega al técnico.
const franjaTxt = (f) => (/^\d{2}:\d{2}-\d{2}:\d{2}$/.test(f || '') ? `${Number(f.slice(0, 2))} a ${Number(f.slice(6, 8))}` : f || 'sin hora');

function ubicacion() {
    return new Promise((ok) => {
        if (!navigator.geolocation) { ok(null); return; }
        navigator.geolocation.getCurrentPosition(p => ok({ lat: p.coords.latitude, lng: p.coords.longitude }), () => ok(null),
            { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 });
    });
}

export default function OrdenarRestoSheet({ tecnicoId, tecnicoNombre, fecha = getTodayISO(), onCerrar, onListo }) {
    const [datos, setDatos] = useState(null);
    const [cargando, setCargando] = useState(true);
    const [gps, setGps] = useState(undefined);        // undefined = buscando · null = sin GPS
    const [respetar, setRespetar] = useState(true);
    const [motivo, setMotivo] = useState(null);
    const [otro, setOtro] = useState('');
    const [guardando, setGuardando] = useState(false);
    const pedido = useRef(0);

    const calcular = useCallback(async (ordenIds = null, pos = gps) => {
        const n = ++pedido.current;
        setCargando(true);
        try {
            const r = await api.post('/mapa/rehacer-ruta', { fecha, tecnicoId, respetar, ordenIds, ...(pos ? pos : {}) });
            if (n === pedido.current) setDatos(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.mensaje || 'No se pudo calcular');
            if (n === pedido.current && !datos) onCerrar();
        } finally { if (n === pedido.current) setCargando(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fecha, tecnicoId, respetar, gps]);

    useEffect(() => { ubicacion().then(setGps); }, []);
    useEffect(() => { if (gps !== undefined) calcular(null, gps); }, [gps, respetar]); // eslint-disable-line react-hooks/exhaustive-deps

    const mover = (i, d) => {
        const ids = datos.paradas.map(p => p.ordenId);
        [ids[i], ids[i + d]] = [ids[i + d], ids[i]];
        setDatos(x => ({ ...x, paradas: ids.map(id => x.paradas.find(p => p.ordenId === id)) }));
        calcular(ids);
    };
    const aplicar = async () => {
        setGuardando(true);
        try {
            const r = await api.post('/mapa/rehacer-ruta/aplicar', {
                fecha, tecnicoId, motivo: motivo === 'Otro' ? (otro.trim() || 'Otro') : motivo,
                paradas: datos.paradas.map(p => ({ ordenId: p.ordenId, franja: p.franja })),
            });
            toast.success(`Orden guardado${r.data.cambiadas ? `: ${r.data.cambiadas} con otra franja` : ''}. Le avisamos a ${tecnicoNombre.split(' ')[0]}.`, { duration: 5000 });
            onListo?.();
        } catch (e) { toast.error(e?.response?.data?.mensaje || 'No se pudo guardar'); }
        finally { setGuardando(false); }
    };

    const ahorro = datos ? Math.round((datos.kmAntes - datos.kmDespues) * 10) / 10 : 0;
    // Mapa del recorrido (29-oct-2026): dónde estás, el orden numerado y dónde terminás
    const enMapa = datos ? datos.paradas.filter(p => p.lat != null && p.lng != null) : [];
    const ult = enMapa[enMapa.length - 1];
    const puntosMapa = !datos ? [] : [
        ...(datos.origen?.lat != null ? [{ id: 'origen', lat: datos.origen.lat, lng: datos.origen.lng, color: '#2563EB', etiqueta: '•',
            titulo: datos.deDonde === 'GPS' ? 'Estás acá' : 'Salís de acá', tituloFijo: true }] : []),
        ...enMapa.map((p, i) => ({ id: p.ordenId, lat: p.lat, lng: p.lng, color: p === ult ? '#16A34A' : '#C9341F', etiqueta: String(i + 1),
            titulo: p === ult ? `Terminás acá · ${datos.terminaA}` : `${i + 1}. ${p.nombre} · ${p.llegada}`, tituloFijo: p === ult })),
    ];
    const lineaMapa = puntosMapa.map(p => [p.lat, p.lng]);
    const desdeAca = async () => { setGps(undefined); const pos = await ubicacion(); setGps(pos); };
    const abrirMaps = () => {
        const url = linkRuta(enMapa.map(p => ({ direccion: p.direccion, lat: p.lat, lng: p.lng })), datos?.deDonde === 'GPS' ? datos.origen : null);
        if (url) window.open(url, '_blank', 'noopener');
    };
    const chip = (on) => `h-9 px-3 rounded-full text-label font-black border active:scale-95 ${on ? 'border-brand-red bg-[rgba(232,66,47,0.10)] text-ink' : 'border-black/10 dark:border-white/10 text-secondary'}`;

    return (
        <ModalShell titulo="Ordenar lo que falta" subtitulo={`${tecnicoNombre.split(' ')[0]} · hoy${datos ? ` · ${datos.hechas} hecha${datos.hechas !== 1 ? 's' : ''}` : ''}`}
            onCerrar={onCerrar}
            pie={datos && (
                <button type="button" onClick={aplicar} disabled={guardando || cargando}
                    className="w-full h-12 rounded-xl bg-brand-red text-white text-body font-black active:scale-95 disabled:opacity-50">
                    {guardando ? 'Guardando…' : 'Guardar este orden'}
                </button>
            )}>
            {!datos ? (
                <div className="py-10 text-center text-body text-muted">{gps === undefined ? 'Buscando dónde estás…' : 'Armando el mejor orden…'}</div>
            ) : (
                <div className="space-y-4">
                    {puntosMapa.length > 1 && (
                        <div className="rounded-2xl overflow-hidden border border-black/[0.06] dark:border-white/[0.06]">
                            <MapaLeaflet puntos={puntosMapa} lineas={lineaMapa} ajustarKey={puntosMapa.map(p => p.id).join('-')} className="w-full h-64" />
                            <div className="flex gap-2 p-2 bg-panel">
                                <button type="button" onClick={desdeAca} disabled={cargando}
                                    className="flex-1 h-10 rounded-xl bg-chip text-ink text-label font-black inline-flex items-center justify-center gap-1.5 active:scale-95 disabled:opacity-40">
                                    <LuLocateFixed size={15} /> Recalcular desde donde estoy
                                </button>
                            </div>
                            <div className="flex gap-2 px-2 pb-2 bg-panel">
                                <button type="button" onClick={abrirMaps}
                                    className="flex-1 h-10 rounded-xl bg-chip text-ink text-label font-black inline-flex items-center justify-center gap-1.5 active:scale-95">
                                    <LuNavigation size={15} /> Google Maps
                                </button>
                                {datos.paradas[0] && (
                                    <a href={linkWaze(datos.paradas[0].direccion, datos.paradas[0].lat, datos.paradas[0].lng)} target="_blank" rel="noreferrer"
                                        className="flex-1 h-10 rounded-xl bg-[#33CCFF] text-[#0B2A3A] text-label font-black inline-flex items-center justify-center gap-1.5 active:scale-95">
                                        <LuNavigation size={15} /> Waze · ir a la 1
                                    </a>
                                )}
                            </div>
                        </div>
                    )}
                    <div className="rounded-2xl bg-panel p-3.5 space-y-1">
                        <p className="flex items-center gap-1.5 text-body font-bold text-ink"><LuMapPin size={15} className="shrink-0" /> Sale de: {datos.origen.nombre}</p>
                        <p className="text-caption text-muted">
                            Desde las {datos.desde} · termina {datos.terminaA}
                            {ahorro > 0.4 ? <span className="font-black text-[var(--etapa-listo-tx)]"> · {ahorro} km menos que el orden de antes</span> : ''}
                        </p>
                        {datos.deDonde !== 'GPS' && <p className="text-caption text-muted">Sin la ubicación del celu: sale de {datos.deDonde === 'BASE' ? 'la base' : 'la última visita'}.</p>}
                    </div>

                    <ol className="space-y-2">
                        {datos.paradas.map((p, i) => (
                            <li key={p.ordenId} className={`flex items-center gap-2.5 p-3 rounded-2xl bg-card border ${p.tarde || p.atrasada ? 'border-[var(--alerta)]/50' : 'border-black/[0.06] dark:border-white/[0.06]'} ${cargando ? 'opacity-70' : ''}`}>
                                <span className="w-7 h-7 rounded-full shrink-0 bg-brand-red text-white text-label font-black inline-flex items-center justify-center">{i + 1}</span>
                                <span className="flex-1 min-w-0">
                                    <span className="block text-body-lg font-black text-ink truncate">{p.nombre}</span>
                                    <span className="block text-caption text-muted truncate">{[p.motivo, String(p.direccion || '').split(',')[0], `${p.km} km`].filter(Boolean).join(' · ')}</span>
                                    <span className="block text-caption font-bold text-secondary">
                                        Llega {p.llegada} · {franjaTxt(p.franja)}
                                        {p.cambiaFranja && <span className="text-[#B45309] dark:text-[#F0A500]"> (antes {franjaTxt(p.horaAntes)})</span>}
                                    </span>
                                    {p.porque.length > 0 && (
                                        <span className={`block text-caption font-bold ${p.tarde || p.atrasada ? 'text-[var(--alerta-tx)]' : 'text-muted'}`}>
                                            {(p.tarde || p.atrasada) && <LuTriangleAlert size={12} className="inline mr-1 -mt-0.5" />}{p.porque.join(' · ')}
                                        </span>
                                    )}
                                </span>
                                <span className="flex flex-col gap-1 shrink-0">
                                    <button type="button" onClick={() => mover(i, -1)} disabled={i === 0 || cargando} aria-label="Subir"
                                        className="w-10 h-9 rounded-lg bg-chip text-secondary flex items-center justify-center disabled:opacity-30"><LuChevronUp size={17} /></button>
                                    <button type="button" onClick={() => mover(i, 1)} disabled={i === datos.paradas.length - 1 || cargando} aria-label="Bajar"
                                        className="w-10 h-9 rounded-lg bg-chip text-secondary flex items-center justify-center disabled:opacity-30"><LuChevronDown size={17} /></button>
                                </span>
                            </li>
                        ))}
                    </ol>
                    {datos.metodo === 'MANUAL' && (
                        <button type="button" onClick={() => calcular(null)} className="w-full h-10 rounded-xl bg-chip text-ink text-label font-black inline-flex items-center justify-center gap-1.5 active:scale-95">
                            <span aria-hidden>✨</span> Volver al orden automático
                        </button>
                    )}
                    {datos.sinUbicar.length > 0 && (
                        <p className="text-caption text-muted">Sin ubicar en el mapa (quedan al final): {datos.sinUbicar.map(x => x.nombre).join(', ')}</p>
                    )}

                    <label className="flex items-center justify-between gap-3 p-3 rounded-2xl bg-panel">
                        <span className="min-w-0">
                            <span className="block text-body font-bold text-ink">Respetar la franja de cada cliente</span>
                            <span className="block text-caption text-muted">Apagado: el camino más corto, aunque cambie horarios acordados</span>
                        </span>
                        <input type="checkbox" checked={respetar} onChange={e => setRespetar(e.target.checked)} className="w-6 h-6 accent-[#C9341F] shrink-0" />
                    </label>

                    <div className="space-y-2">
                        <p className="px-1 text-caption font-bold text-muted">Motivo (queda en el historial y le llega al técnico)</p>
                        <div className="flex flex-wrap gap-1.5">
                            {datos.motivos.map(m => (
                                <button key={m} type="button" onClick={() => setMotivo(x => (x === m ? null : m))} aria-pressed={motivo === m} className={chip(motivo === m)}>{m}</button>
                            ))}
                        </div>
                        {motivo === 'Otro' && (
                            <input value={otro} onChange={e => setOtro(e.target.value)} maxLength={200} placeholder="Ej.: Laura pidió que vaya después de las 14"
                                className="w-full h-11 px-3 rounded-xl bg-page text-ink text-body outline-none border border-black/10 dark:border-white/10 placeholder:text-muted" />
                        )}
                    </div>
                    <p className="flex items-center gap-1.5 text-caption text-muted"><LuNavigation size={13} /> Después, "Google Maps" lleva solo lo que falta, en este orden.</p>
                </div>
            )}
        </ModalShell>
    );
}
